import React, { type AriaAttributes } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, injectIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';

import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/glyph/search';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import { N200, N90 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

export const i18n = defineMessages({
	searchBarLabel: {
		id: 'debounced-search-bar.label',
		defaultMessage: 'Search',
		description: 'A visible label for the search bar that allows the user to filter items',
	},
	searchPlaceholderText: {
		id: 'debounced-search-bar.hintText',
		defaultMessage: 'Search',
		description: 'The placeholder text in the search box, visible when it is empty',
	},
	searchIconLabel: {
		id: 'debounced-search-bar.search.icon.label',
		defaultMessage: 'Search icon',
		description: 'The aria-label for the magnifying glass icon in the search bar',
	},
	crossIconLabel: {
		id: 'debounced-search-bar.cross.icon.label',
		defaultMessage: 'Clear search bar',
		description: 'The aria-label for the cross icon in the search bar',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchIconWrapper = styled.div({
	margin: `1px ${token('space.075', '6px')} 0 ${token('space.100', '8px')}`,
	color: token('color.icon.subtle', N90),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CrossIconWrapper = styled.button<{
	isDisabled?: boolean;
	isVisible: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: (props) => (props.isVisible ? 'inline-block' : 'none'),
	margin: `0px ${token('space.075', '6px')}`,
	padding: 0,
	background: 'none',
	border: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: (props) => (props.isDisabled ? 'inherit' : 'pointer'),
});

type DebouncedSearchBarProps = {
	onChange: (searchQuery: string) => void;
	onFocus?: () => void;
	onBlur?: () => void;

	initialValue?: string;
	label?: string;
	placeholder?: string;
	testId?: string;
	debouncedTime?: number;

	isDisabled?: boolean;
	isCompact?: boolean;
	autoFocus?: boolean;
	ariaLabelledBy?: string;
	ariaLive?: AriaAttributes['aria-live'];
};

type DebouncedSearchBarState = {
	value: string;
};

type InjectedDebouncedSearchBarProps = WrappedComponentProps & DebouncedSearchBarProps;

class InjectedDebouncedSearchBar extends React.Component<
	InjectedDebouncedSearchBarProps,
	DebouncedSearchBarState
> {
	static defaultProps = {
		isCompact: true,
		debouncedTime: 250,
	};

	state = {
		value: this.props.initialValue || '',
	};

	searchBarRef = React.createRef<HTMLInputElement>();

	_debouncedUpdateSearch = debounce((newSearchQuery: string): void => {
		this.props.onChange(newSearchQuery);
	}, this.props.debouncedTime);

	handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		this.setState({ value: e.target.value });
		this._debouncedUpdateSearch(e.target.value);
	};

	handleBlur = (): void => {
		const { onBlur } = this.props;
		this._debouncedUpdateSearch.flush();
		onBlur?.();
	};

	handleClearClick = (e) => {
		e.preventDefault();
		if (this.props.isDisabled) {
			return;
		}
		this.setState({ value: '' });
		this._debouncedUpdateSearch('');
		this._debouncedUpdateSearch.flush();

		this.searchBarRef.current?.focus();
	};

	render() {
		const {
			intl,
			onFocus,
			label = intl.formatMessage(i18n.searchBarLabel),
			placeholder = intl.formatMessage(i18n.searchPlaceholderText),
			testId,
			isDisabled,
			isCompact,
			autoFocus,
			ariaLabelledBy,
			ariaLive,
		} = this.props;
		const { value } = this.state;

		/* eslint-disable jsx-a11y/no-autofocus */
		return (
			<Textfield
				ref={this.searchBarRef}
				label={label}
				testId={testId}
				placeholder={placeholder}
				value={value}
				onChange={this.handleChange}
				onBlur={this.handleBlur}
				elemBeforeInput={
					<SearchIconWrapper>
						<SearchIcon label={intl.formatMessage(i18n.searchIconLabel)} size="small" />
					</SearchIconWrapper>
				}
				elemAfterInput={
					<CrossIconWrapper
						data-testid="clear-button"
						isDisabled={isDisabled}
						isVisible={!!value}
						onClick={this.handleClearClick}
					>
						<CrossCircleIcon
							label={intl.formatMessage(i18n.crossIconLabel)}
							primaryColor={
								isDisabled ? token('color.icon.disabled', N90) : token('color.icon.subtle', N200)
							}
							size="small"
						/>
					</CrossIconWrapper>
				}
				isDisabled={isDisabled}
				onFocus={onFocus}
				isCompact={isCompact}
				autoFocus={autoFocus}
				aria-labelledby={ariaLabelledBy}
				aria-live={ariaLive}
			/>
		);
	}
}

export const DebouncedSearchBar = injectIntl(InjectedDebouncedSearchBar);
